// Assuming ObjectId is represented as string in frontend context
type ObjectId = string;

enum EnachTokenChargeStatus {
  INITIATED = 'INITIATED',
  CHARGED = 'CHARGED',
  FAILED = 'FAILED'
}

enum AdminUserRoles {
  FEE_CLERK = 'FEE_CLERK',
  FEE_ADMIN = 'FEE_ADMIN'
}

enum TransactionStatus {
  NOT_DEPOSITED = 'NOT DEPOSITED',
  CLEARED = 'CLEARED',
  DEPOSITED = 'DEPOSITED',
  BOUNCED = 'BOUNCED',
  CANCELLED = 'CANCELLED'
}

enum ModeOfPayment {
  CREDIT_CARD = 'Credit Card',
  DEBIT_CARD = 'Debit Card',
  NET_BANKING = 'Net Banking',
  E_MANDATE = 'E-Mandate',
  CASH = 'Cash',
  CHEQUE = 'Cheque',
  UPI = 'UPI',
  WALLET = 'Wallet',
  OTHER_CARD = 'Other Card',
  PAY_LATER = 'Pay Later',
  CARDLESS = 'Cardless',
  DEMAND_DRAFT = 'Demand Draft',
  EMI = 'EMI'
}
enum PaymentStatus {
  PENDING = 'PENDING',
  INITIATED = 'INITIATED',
  RECEIVED = 'RECEIVED'
}
enum PaymentSchemeDiscounts {
  WAIVERS = 'WAIVERS',
  CONCESSIONS = 'CONCESSIONS'
}

enum PaymentSchemeFines {
  LATE_FEE = 'LATE_FEE',
  CHEQUE_BOUNCE_FEE = 'CHEQUE_BOUNCE_FEE'
}

enum PaymentAddonsStatus {
  IN_APPROVAL = 'IN_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

interface PaymentAddons {
  id: string;
  description: string;
  amount: number;
  status?: PaymentAddonsStatus;
  createdAt?: Date;
  createdBy?: ObjectId;
}

interface PaymentSchemeStatusLog {
  id: ObjectId;
  paymentSchemeId: ObjectId;
  paymentStatus: PaymentStatus;
  remark: string;
  updatedBy: ObjectId;
  statusChangeDate?: Date;
  createdAt?: string;
  updatedAt?: string;
}

interface TransactionStatusLog {
  id: ObjectId;
  paymentDate: Date;
  transactionId: ObjectId;
  status: TransactionStatus;
  remark: string;
  failureReason: string;
  updatedBy: ObjectId;
  statusChangeDate: Date;
  createdAt?: string;
  updatedAt?: string;
}

type AdditionalConcessionsAndWaivers = {
  id: string;
  type: PaymentSchemeDiscounts;
  appliedOn: PaymentSchemeFines;
  description: string;
  amount: number;
  status: PaymentAddonsStatus;
  createdAt?: Date;
  createdBy?: ObjectId;
};

interface RazorpayLink {
  shortUrl: string;
  id?: string;
}

interface Cheque {
  id: ObjectId;
  chequeNo: string;
  bank: string;
  branch: string;
  issueDate: Date;
  createdAt?: string;
  updatedAt?: string;
}

interface User {
  id: ObjectId;
  email: string;
  role: AdminUserRoles;
  school: string;
  schools?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

interface PaymentScheme {
  id: ObjectId;
  createdAt: Date;
  updatedAt: Date;
  userId: ObjectId;
  feeId: string;
  amount: number;
  lateFee: number;
  lateFeeUpdatedAt: Date;
  chequeBounceFee: number;
  chequeBounceFeeUpdatedAt: Date;
  paymentDueDate: string;
  taxes: PaymentAddons[];
  concessions: PaymentAddons[];
  waivers: PaymentAddons[];
  additionalConcessionsAndWaivers?: AdditionalConcessionsAndWaivers[];
  particulars: string;
  feePeriod: string;
  scheme: string;
  school: string;
  demandId: string;
  description?: string;
  modeOfPayment?: string;
  paymentStatus?: PaymentStatus;
  isSelected?: boolean;
  grade?: string;
  division?: string;
  session?: string;
  remark?: string;
  updatedBy: ObjectId;
  component: string;
  generatePaymentLink?: boolean;
  razorpayLink?: RazorpayLink;
  enachRegistrationDetails?: {
    customer_id: string;
    token_id: string;
    tokenChargeStatus?: EnachTokenChargeStatus;
    paymentId?: string;
    updatedAt?: Date;
  };
  originalAmount?: number;
  isFirstInstallmentOrFullPayment?: boolean;
}

export interface FeeFineDetailsData {
  _id: ObjectId;
  particulars: string;
  feePeriod: string;
  academicYear: string;
  chequeBounceFee: number;
  lateFee: number;
  paidLateFee: number;
  pendingLateFee?: number;
  paidChequeBounceFee: number;
  pendingChequeBounceFee?: number;
  dueDays?: number;
  dueMonths?: number;
  additionalConcessionsAndWaivers?: AdditionalConcessionsAndWaivers[];
  totalLateFee?: string;
  totalLateFeePaid?: string;
  totalChequeBounceFee?: string;
  totalChequeBounceFeePaid?: string;
  totalFinePayable?: string;
  waiversString?: string;
  concessionsString?: string;
  pendingFinePayable?: number;
  dueDate?: string; // Add dueDate property
}

export interface StudentPaymentSchemeWithMetrics {
  _id: ObjectId;
  amount: number;
  lateFee: number;
  scheme: string;
  chequeBounceFee: number;
  paidAmount: number;
  paidLateFee: number;
  paidChequeBounceFee: number;
  paymentDueDate: string; // stored as string per existing schema
  pendingAmount: number; // amount - paidAmount (can be negative when overpaid)
  pendingLateFee?: number; // max(0, lateFee - paidLateFee)
  pendingChequeBounceFee?: number; // max(0, chequeBounceFee - paidChequeBounceFee)
  pendingFinePayable?: number; // pendingLateFee + pendingChequeBounceFee
  userId: ObjectId;
  component: string;
  particulars: string;
  description?: string;
  remark?: string;
  paymentStatus?: PaymentStatus;
  demandId: string;
  feePeriod: string;
  grade?: string;
  academicYear: string; // derived from session
  concessions: PaymentAddons[];
  waivers: PaymentAddons[];
  additionalConcessionsAndWaivers?: AdditionalConcessionsAndWaivers[];
  enachRegistrationDetails?: PaymentScheme['enachRegistrationDetails'];
  dueDays?: number;
  dueMonths?: number;
  'ENACH Registered ?': boolean;
  paymentSchemeStatusLogs?: PaymentSchemeStatusLog[]; // Add paymentSchemeStatusLogs property
  // Formatted aggregate totals (attached to each item for convenience)
  totalAmountPayable: string;
  totalAmountPaid: string;
  totalPendingAmount: string;
  totalLateFee?: string;
  totalLateFeePaid?: string;
  totalChequeBounceFee?: string;
  totalChequeBounceFeePaid?: string;
  totalFinePayable?: string; // pendingLateFee + pendingChequeBounceFee aggregate
}

// (No direct imports needed; kept lean for portability)

// View model for a transaction row in the student's transactions matrix.
// All ObjectIds are stringified for front-end convenience.
export interface StudentTransactionMatrixItem {
  _id: string; // transaction id
  userId: string;
  paymentDate: string; // formatted date (Do MMM YYYY)
  transactionId: string;
  status: string;
  receiptNo?: string;
  modeOfPayment?: string;
  amount: number;
  paymentSchemeId: string;
  feeDepositedUpto: string; // ISO string
  isLateFee: boolean; // derived flag (against === 'LATE_FEE')
  against?: string; // e.g. 'LATE_FEE', 'CHEQUE_BOUNCE_FEE', principal, etc.
  particulars: string; // scheme.particulars - feePeriod
  academicYear: string; // scheme.session
  createdAt?: string;
  updatedAt?: string;
  updatedByUser?: User;
  transactionStatusLogs?: TransactionStatusLog[];
  remark?: string; // Added remark property as per feedback
  failureReason?: string;
  chequeDetails?: Cheque[];
}

// Combined response type for metrics + transactions matrix endpoint.
export interface StudentSchemesAndTransactionsResponse {
  paymentSchemesMatrix: StudentPaymentSchemeWithMetrics[];
  transactionsMatrix: StudentTransactionMatrixItem[];
}

interface ConcessionOrWaiverUpdateRequest {
  paymentSchemeId: string;
  concessions: {
    description: string;
    amount: number;
    id: string;
    status: PaymentAddonsStatus;
  }[];
  waivers: {
    description: string;
    amount: number;
    id: string;
    status: PaymentAddonsStatus;
  }[];
  additionalConcessionsAndWaivers: {
    description: string;
    amount: number;
    id: string;
    status: PaymentAddonsStatus;
    type: string;
    appliedOn: string;
  }[];
}

export {
  PaymentScheme,
  PaymentAddons,
  AdditionalConcessionsAndWaivers,
  RazorpayLink,
  EnachTokenChargeStatus,
  ModeOfPayment,
  PaymentStatus,
  PaymentSchemeDiscounts,
  PaymentSchemeFines,
  PaymentAddonsStatus,
  ConcessionOrWaiverUpdateRequest,
  TransactionStatus,
  AdminUserRoles
};
